import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsExclamationTriangleFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';

import { useConfirmPostMailingWarnings } from '../../../models/campaigns/mutations';
import { usePostMailingSends } from '../../../models/campaigns/queries';

// needs_review letters are typically few; load them all in one page rather than paginating the panel.
const REVIEW_PAGE_SIZE = 500;
const NEEDS_REVIEW_FILTERS = { status: ['needs_review'] };

const formatAddress = (address) => {
	if (!address) return '-';
	const line2 = [address.postal, address.city].filter(Boolean).join(' ');
	return [address.street, line2].filter(Boolean).join(', ') || '-';
};

// Backend stays English-only; translate its machine-readable send.errorMessage reasons (and the
// eBrief doc statuses that can surface here) to German.
const WARNING_LABELS = {
	ADDRESS_OVERFLOW:
		'Adresse zu lang für das Adressfenster — Schrift wurde verkleinert, passt evtl. nicht vollständig.',
	WARNING_USER_INTERACTION_REQUIRED:
		'Der Versanddienst konnte die Anschrift nicht eindeutig erkennen — bitte prüfen.',
};

/**
 * German hint text for a review row: mapped reason; unknown machine codes are wrapped in a German
 * sentence instead of shown raw.
 * @param {{ errorMessage?: string, ebriefDocStatus?: string }} send
 * @returns {string}
 */
const warningLabel = (send) => {
	const code = send.errorMessage || send.ebriefDocStatus;
	if (WARNING_LABELS[code]) return WARNING_LABELS[code];
	return code ? `Warnung des Versanddiensts (${code}) — bitte Anschrift prüfen.` : 'Bitte Anschrift prüfen.';
};

// Per-letter decision toggle: "Freigeben" (send anyway) vs "Überspringen" (cancel).
const SEGMENT_BASE = 'px-3 py-1 text-xs font-medium transition-colors';

function DecisionToggle({ value, onChange }) {
	return (
		<div className="inline-flex overflow-hidden rounded-control border border-bgBase-500">
			<button
				type="button"
				className={`${SEGMENT_BASE} ${
					value === 'confirm' ? 'bg-success-700 text-white' : 'bg-white text-text hover:bg-bgBase-300'
				}`}
				onClick={() => onChange('confirm')}
			>
				Freigeben
			</button>
			<button
				type="button"
				className={`${SEGMENT_BASE} border-l border-bgBase-500 ${
					value === 'skip' ? 'bg-bgBase-800 text-white' : 'bg-white text-text hover:bg-bgBase-300'
				}`}
				onClick={() => onChange('skip')}
			>
				Überspringen
			</button>
		</div>
	);
}
DecisionToggle.propTypes = {
	value: PropTypes.oneOf(['confirm', 'skip']),
	onChange: PropTypes.func.isRequired,
};

/**
 * Lists the letters eBrief flagged with an address warning (status 'needs_review') and lets the user
 * confirm or skip each one, then submits the decisions so the campaign can proceed.
 */
const PostMailingReviewPanel = ({ campaignId, enabled = true }) => {
	const { hasCapability: hasSendCapability } = useCapabilityCheck('postmailing.send');
	// Load only the needs_review letters via the paginated sends endpoint (own filter, big page).
	const { data: reviewData, isLoading } = usePostMailingSends(campaignId, {
		filters: NEEDS_REVIEW_FILTERS,
		page: 1,
		pageSize: REVIEW_PAGE_SIZE,
		enabled,
	});
	const reviewSends = reviewData?.sends ?? [];
	// Map of sendId → 'confirm' | 'skip'. Undecided letters are not submitted.
	const [decisions, setDecisions] = useState({});
	const [showSkipConfirm, setShowSkipConfirm] = useState(false);
	const confirmWarnings = useConfirmPostMailingWarnings();

	// The "Zur Prüfung" banner points here — show a skeleton instead of nothing while loading.
	if (enabled && isLoading) {
		return (
			<Card className="border-caution-500/40 bg-caution-200/40">
				<div className="flex items-center gap-3 py-2">
					<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-caution-700" />
					<span className="text-sm text-text">Zu prüfende Briefe werden geladen …</span>
				</div>
			</Card>
		);
	}

	if (reviewSends.length === 0) return null;

	const setDecision = (sendId, decision) => setDecisions((prev) => ({ ...prev, [sendId]: decision }));
	const setAll = (decision) => setDecisions(Object.fromEntries(reviewSends.map((s) => [s.id, decision])));

	const confirm = reviewSends.filter((s) => decisions[s.id] === 'confirm').map((s) => s.id);
	const skip = reviewSends.filter((s) => decisions[s.id] === 'skip').map((s) => s.id);
	const decidedCount = confirm.length + skip.length;

	const submitDecisions = async () => {
		try {
			await confirmWarnings.mutateAsync({ campaignId, confirm, skip });
			setDecisions({});
		} catch {
			// Error toast handled by the global MutationCache.
		} finally {
			setShowSkipConfirm(false);
		}
	};

	// Skipping cancels letters irreversibly — gate that behind an explicit confirmation.
	const handleSubmit = () => {
		if (decidedCount === 0) return;
		if (skip.length > 0) {
			setShowSkipConfirm(true);
			return;
		}
		submitDecisions();
	};

	return (
		<Card className="border-caution-500/40 bg-caution-200/40">
			<div className="mb-4 flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<BsExclamationTriangleFill className="mt-0.5 h-5 w-5 flex-shrink-0 text-caution-700" />
					<div>
						<h3 className="text-md font-semibold tracking-tight">Adressprüfung ({reviewSends.length})</h3>
						<p className="mt-0.5 text-sm text-text">
							Geben Sie zweifelhafte Anschriften frei oder überspringen Sie sie. Übersprungene Briefe
							werden nicht versendet.
						</p>
					</div>
				</div>
			</div>

			<div className="mb-3 flex items-center gap-2">
				<Button variant="outline" size="sm" onClick={() => setAll('confirm')}>
					Alle freigeben
				</Button>
				<Button variant="outline" size="sm" onClick={() => setAll('skip')}>
					Alle überspringen
				</Button>
			</div>

			<div className="overflow-x-auto rounded-card border border-bgBase-500 bg-white">
				<table className="w-full text-sm">
					<thead className="border-b border-bgBase-500 bg-bgBase-300">
						<tr>
							<th className="px-4 py-3 text-left font-medium text-text">Empfänger</th>
							<th className="px-4 py-3 text-left font-medium text-text">Adresse</th>
							<th className="px-4 py-3 text-left font-medium text-text">Hinweis</th>
							<th className="px-4 py-3 text-left font-medium text-text">Entscheidung</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-bgBase-500">
						{reviewSends.map((send) => (
							<tr key={send.id} className="hover:bg-bgBase-300">
								<td className="px-4 py-3 text-text">{send.recipientName || '-'}</td>
								<td className="px-4 py-3 text-text">{formatAddress(send.recipientAddress)}</td>
								<td className="px-4 py-3 text-bgBase-800">{warningLabel(send)}</td>
								<td className="px-4 py-3">
									<DecisionToggle
										value={decisions[send.id]}
										onChange={(d) => setDecision(send.id, d)}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="mt-4 flex items-center justify-end gap-3">
				<span className="text-xs text-bgBase-800">
					{decidedCount} von {reviewSends.length} entschieden
				</span>
				<Button
					variant="solid"
					onClick={handleSubmit}
					disabled={decidedCount === 0 || confirmWarnings.isPending || !hasSendCapability}
					title={hasSendCapability ? '' : 'Briefversand ist in Ihrem aktuellen Abo nicht verfügbar'}
					loading={confirmWarnings.isPending}
				>
					Auswahl bestätigen
				</Button>
			</div>

			<ConfirmModal
				isOpen={showSkipConfirm}
				onClose={() => setShowSkipConfirm(false)}
				onConfirm={submitDecisions}
				title="Briefe überspringen"
				message={`${skip.length} ${skip.length === 1 ? 'Brief wird' : 'Briefe werden'} übersprungen und nicht versendet — das lässt sich nicht rückgängig machen. Bereits bezahlte Beträge für übersprungene Briefe werden automatisch erstattet.${confirm.length > 0 ? ` ${confirm.length} ${confirm.length === 1 ? 'Brief wird' : 'Briefe werden'} freigegeben.` : ''}`}
				confirmText="Auswahl bestätigen"
				cancelText="Zurück"
				isLoading={confirmWarnings.isPending}
			/>
		</Card>
	);
};

PostMailingReviewPanel.propTypes = {
	campaignId: PropTypes.string.isRequired,
	enabled: PropTypes.bool,
};

export default PostMailingReviewPanel;
